import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Layout } from "../components/Layout";
import { Skeleton } from "../components/ui/skeleton";
import { num, fmtDate } from "../lib/format";
import { CustomerHeader, RunSelect } from "./PayrollDashboard";

const money = (v) => (v == null || isNaN(v) ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

function PivotTable({ title, testid, codes, rows, kind }) {
  const fmt = kind === "hours" ? (v) => (v ? num(v, 2) : "—") : (v) => (v ? money(v) : "—");
  return (
    <section className="mt-8">
      <h2 className="mb-2 font-heading text-base font-bold uppercase tracking-wide">{title}</h2>
      {rows.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-6 text-center text-sm text-muted-foreground">No {kind === "hours" ? "hourly" : "salaried"} employees in this register.</div>
      ) : (
        <div className="overflow-auto rounded-lg border border-border bg-card max-h-[32rem]">
          <table className="tbl-sticky w-full text-sm" data-testid={testid}>
            <thead>
              <tr className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2 text-left font-semibold">Name</th>
                {codes.map((c) => <th key={c} className="px-3 py-2 text-right font-semibold">{c}</th>)}
                <th className="px-3 py-2 text-right font-semibold">{kind === "hours" ? "Total Hrs" : "Total Gross"}</th>
                {kind === "hours" ? <th className="px-3 py-2 text-right font-semibold">Prior Total Hrs</th> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-border hover:bg-card-hover">
                  <td className="px-3 py-1.5 font-medium">{r.name}</td>
                  {codes.map((c) => <td key={c} className="px-3 py-1.5 text-right font-mono">{fmt(r.cells[c])}</td>)}
                  <td className="px-3 py-1.5 text-right font-mono font-bold">{fmt(r.total)}</td>
                  {kind === "hours" ? <td className="px-3 py-1.5 text-right font-mono text-muted-foreground">{fmt(r.prior_total)}</td> : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function PYResults() {
  const { clientId } = useParams();
  const [runId, setRunId] = useState(null);
  const { data: client, isLoading: cl } = useQuery({
    queryKey: ["client", clientId], queryFn: async () => (await api.get(`/clients/${clientId}`)).data,
  });
  const runs = client?.runs || [];
  const activeRun = runId || runs[0]?.payroll_run_GUID;
  const { data, isLoading } = useQuery({
    queryKey: ["py-results", activeRun],
    queryFn: async () => (await api.get(`/runs/${activeRun}/py-results`)).data,
    enabled: !!activeRun,
  });

  if (cl) return <Layout><Skeleton className="h-8 w-72" /><Skeleton className="mt-6 h-96 rounded-md" /></Layout>;
  const c = client?.client || {};

  return (
    <Layout>
      <CustomerHeader clientId={clientId} c={c} active="py" />
      {runs.length === 0 ? (
        <div className="mt-8 rounded-md border border-border bg-card p-8 text-center text-muted-foreground">No payroll runs yet.</div>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <RunSelect runs={runs} value={activeRun} onChange={setRunId} />
            {data?.run ? <span className="font-mono text-xs text-muted-foreground">Register <b className="text-foreground">{data.run.run_label}</b> · {fmtDate(data.run.pay_date)}</span> : null}
          </div>
          {isLoading ? <Skeleton className="mt-6 h-96 rounded-md" /> : data ? (
            <>
              <PivotTable title="Hourly Employees — Hours by Type" testid="py-hourly" codes={data.hourly.codes} rows={data.hourly.rows} kind="hours" />
              <PivotTable title="Salaried Employees — Gross by Type" testid="py-salaried" codes={data.salaried.codes} rows={data.salaried.rows} kind="gross" />
            </>
          ) : null}
        </>
      )}
    </Layout>
  );
}
